import { ApolloError, useReactiveVar } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery'
import React from 'react'

import {
    GetProjectNuggetContentViewQuery,
    GetProjectProductFamilyViewQuery,
    NuggetContentViewQuery,
    ProductFamilyViewQuery,
} from 'api/graphQL/queries/projectElements.queries'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import { useSuggestionsStore } from 'components/WorkspaceEditor/SuggestionStore'
import { getActiveTabLabel } from 'components/WorkspaceEditor/utils'
import { useParams } from 'react-router-dom'
import { useElementItemStore } from '../../ElementItemStore'
import {
    GetNuggetContentViewResponse,
    GetProductFamilyViewResponse,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
    PartType,
} from '../../types/types'
import { useShallowSelector } from '../../Workspace2Store'
import {
    FetchContentItemProps,
    useProductFetcherUtils,
} from './useProductFetcher.utils'

export interface ProductFetcherProps {
    projectId?: string
    overrideContentItemNodes?: boolean
    callback?: (
        response:
            | GetProjectProductFamilyViewResponse
            | GetProjectNuggetContentViewResponse
    ) => Promise<void>
    onError?: () => void
}

export const useProductFetcher = ({
    projectId = '',
    overrideContentItemNodes = false,
    callback,
    onError, // Default error handler
}: ProductFetcherProps) => {
    const {
        fetchContentItem,
        handleFetchError,
        onContentViewFetchSuccess,
    } = useProductFetcherUtils({
        overrideContentItemNodes,
        projectId,
    })

    const isFromSMM = isReactiveContentTeamFromStudioPage()?.value
    const { isTrackChangeToggleOn } = useReactiveVar(showHideToggles)
    const {
        contentItemViewNodes,
        articlesLoading,
        setArticlesLoading,
        setIsTreeLoading,
        activeTab,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        articlesLoading: state.articlesLoading,
        setArticlesLoading: state.actions.setArticlesLoading,
        setIsTreeLoading: state.actions.setIsTreeLoading,
        activeTab: state.activeTab,
    }))
    const {
        elementId,
        isProjectProductFamily: isPPF,
        id: nodeId,
        isProductTypeProject,
    } = useElementItemStore((s) => s.elementItemValue)
    const setIsSuggestionsDataUpdated = useSuggestionsStore(
        (s) => s.setIsSuggestionsDataUpdated
    )
    const projectIdFromUrl = useParams<{ projectId?: string }>().projectId

    const finishLoading = (isFamily?: boolean) => {
        setIsSuggestionsDataUpdated(!isFamily)
        setIsTreeLoading(false)
    }

    const [getProjectProductFamilyView] = useLazyQuery<
        GetProjectProductFamilyViewResponse
    >(GetProjectProductFamilyViewQuery(6), {
        fetchPolicy: 'no-cache',
        onCompleted: async (response: GetProjectProductFamilyViewResponse) => {
            if (callback) {
                await callback(response)
            } else {
                onContentViewFetchSuccess(response, articlesLoading).then(
                    () => {
                        finishLoading()
                    }
                )
            }
        },
        onError: (error: ApolloError) => {
            handleFetchError(error, setArticlesLoading, onError)
            finishLoading()
        },
    })

    const [getProductFamilyView] = useLazyQuery<GetProductFamilyViewResponse>(
        ProductFamilyViewQuery(6),
        {
            fetchPolicy: 'no-cache',
            onCompleted: (response: GetProductFamilyViewResponse) => {
                onContentViewFetchSuccess(response, articlesLoading).then(
                    () => {
                        finishLoading(true)
                    }
                )
            },
            onError: (error: ApolloError) => {
                handleFetchError(error, setArticlesLoading, onError)
                finishLoading(true)
            },
        }
    )

    const [getProjectProductTypeView] = useLazyQuery<
        GetProjectNuggetContentViewResponse
    >(GetProjectNuggetContentViewQuery(6), {
        fetchPolicy: 'no-cache',
        onCompleted: async (response: GetProjectNuggetContentViewResponse) => {
            if (callback) {
                await callback(response)
            } else {
                onContentViewFetchSuccess(response, articlesLoading).then(
                    () => {
                        finishLoading()
                    }
                )
            }
        },
        onError: (error: ApolloError) => {
            handleFetchError(error, setArticlesLoading, onError)
            finishLoading()
        },
    })

    const [getProductTypeView] = useLazyQuery<GetNuggetContentViewResponse>(
        NuggetContentViewQuery(6),
        {
            fetchPolicy: 'no-cache',
            onCompleted: (response: GetNuggetContentViewResponse) => {
                onContentViewFetchSuccess(response, articlesLoading).then(
                    () => {
                        finishLoading(true)
                    }
                )
            },
            onError: (error: ApolloError) => {
                handleFetchError(error, setArticlesLoading, onError)
                finishLoading(true)
            },
        }
    )

    const fetchFamilyContentItemView = React.useCallback(
        async (
            partType: PartType,
            rootArticleContentItemIds?: string[]
        ): Promise<void> => {
            const loadProduct = isPPF
                ? getProjectProductFamilyView
                : getProductFamilyView

            const fetchContentItemProps: FetchContentItemProps = {
                partType,
                loadProduct,
                rootArticleContentItemIds,
            }

            return fetchContentItem(fetchContentItemProps)
        },
        [
            projectId,
            isPPF,
            isFromSMM,
            nodeId,
            elementId,
            fetchContentItem,
            isTrackChangeToggleOn,
        ]
    )

    const fetchProductTypeContentItemView = React.useCallback(
        async (
            partType: PartType,
            rootArticleContentItemIds?: string[]
        ): Promise<void> => {
            const loadProduct = isPPF
                ? getProjectProductTypeView
                : getProductTypeView

            const fetchContentItemProps: FetchContentItemProps = {
                partType,
                loadProduct,
                rootArticleContentItemIds,
            }

            return fetchContentItem(fetchContentItemProps)
        },
        [
            projectId,
            isPPF,
            isFromSMM,
            nodeId,
            elementId,
            fetchContentItem,
            isTrackChangeToggleOn,
        ]
    )

    const getContentItemViewNodesAPI = React.useCallback(
        async (
            rootArticleContentItemIds: string[] | null,
            activeTabIndex?: number
        ): Promise<void> => {
            const loadProduct = isProductTypeProject
                ? getProjectProductTypeView
                : getProjectProductFamilyView
            const queryVariables: Record<string, any> = {
                id: elementId,
                projectId: projectId || projectIdFromUrl,
                partType: getActiveTabLabel(activeTabIndex ?? activeTab),
                rootArticleContentItemIds,
                isTrackChangeToggleOn: isTrackChangeToggleOn || false,
                ...(isPPF && { elementId }),
                ...(nodeId && { nodeId }),
                ...(isFromSMM && { isFromSMM }),
            }

            return loadProduct({
                variables: queryVariables,
            })
        },
        [
            projectId,
            isPPF,
            isFromSMM,
            nodeId,
            elementId,
            contentItemViewNodes,
            isTrackChangeToggleOn,
            activeTab,
        ]
    )

    return {
        fetchFamilyContentItemView,
        fetchProductTypeContentItemView,
        getContentItemViewNodesAPI,
    }
}
